/**
 * Face-edge kernel — PUSH/PULL (K2, docs/bim-plan/21 §4.2): the SketchUp moment.
 *
 * extrudeFace translates a face along its own normal and builds the swept side
 * faces from its boundary loops. Everything else falls out of K0's machinery:
 *
 * - Pulling a lone flat face leaves its old boundary ring behind, and HEALING
 *   closes it into the bottom face — a rectangle pulled up IS a closed box,
 *   SketchUp exactly. (Heal runs only on ring edges with exactly ONE face user,
 *   which is precisely what distinguishes it from…)
 * - …a POCKET: pushing a sub-face down leaves its ring shared with the
 *   surrounding face (two users) — no heal, the pocket stays open at the top.
 * - Pulling a face whose side sweep is coplanar with an existing neighbor
 *   (a box top pulled taller) DISSOLVES the seam edge — the side reads as one
 *   face, no horizontal line, SketchUp's coplanar merge.
 * - PUSH-THROUGH: landing exactly on an opposing coplanar face's plane inserts
 *   the ring into that plane's arrangement (K0 insertSegment → repartition) and
 *   deletes the punched piece — the classic through-hole, topology not CSG.
 *
 * - FACE MOVE (2026-09-26, Matt: "I can pull out a face, but I can't push it
 *   back in"): when every face around the pushed face runs ALONG the push
 *   direction (a box top between four vertical sides), push/pull just slides the
 *   face — the neighbors stretch or shrink and stay planar, no new geometry. That
 *   is what SketchUp does with a box top, in both directions; see moveFaceAlong.
 *
 * v1 recorded refusals (spike §4.2 scope): a landing that only PARTIALLY
 * overlaps an existing face, a ring whose insertion would subdivide (crosses
 * existing edges on the landing plane), and pushing INTO an adjacent coplanar
 * face's interior when the face-move rule doesn't apply (a neighbor that is NOT
 * parallel to the push — same-plane region subtraction is a later round).
 */

import type { BodyTopology, BodyPt3, BodyVid, BodyEid, BodyFid, BodyFace, BodyLoop, BodyPlane } from './types'
import { facePaint } from './paint'
import {
  WELD_TOL, add, scale, norm, planeDist, planeFromPoints, samePlane, frameForPlane, to2,
} from './geom3'
import type { Pt2 } from './geom3'
import { dot, from2, pointSegDist } from './geom3'
import {
  newFid, weldVertex, addEdge, findEdgeBetween, facesUsingEdge,
  loopPoints, pointInRegion, regionInteriorPoint, gcOrphanVertices, invariants, splitEdge,
} from './topo'
import { insertSegment } from './arrange'
import { traceFaces } from './trace2d'
import type { TraceNode, TraceEdge } from './trace2d'

const PLANE_TOL = WELD_TOL * 2

export type ExtrudeMode = 'out' | 'through' | 'move'

export interface ExtrudeOk {
  ok: true
  mode: ExtrudeMode
  /** the translated cap face ('out' and 'move' — a through-punch has no cap) */
  capFid: BodyFid | null
  addedFaces: BodyFid[]
  removedFaces: BodyFid[]
}
export interface ExtrudeErr {
  ok: false
  /** user-facing — the status bar shows it verbatim */
  error: string
}
export type ExtrudeResult = ExtrudeOk | ExtrudeErr

/** the face whose plane and region contain p — smallest area wins a multi-plane tie */
export function faceAt(topo: BodyTopology, p: BodyPt3): BodyFid | null {
  let best: BodyFid | null = null
  let bestArea = Infinity
  for (const [fid, f] of Object.entries(topo.faces)) {
    if (Math.abs(planeDist(f.plane, p)) > PLANE_TOL) continue
    const frame = frameForPlane(f.plane)
    const rings = f.loops.map((l) => loopPoints(topo, l).map((q) => to2(frame, q)))
    const pt = to2(frame, p)
    if (!pointInRegion(pt, rings[0], rings.slice(1))) continue
    const area = Math.abs(ringArea(rings[0]))
    if (area < bestArea) { best = fid; bestArea = area }
  }
  return best
}

function ringArea(pts: Pt2[]): number {
  let s = 0
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % pts.length]
    s += a.x * b.y - b.x * a.y
  }
  return s / 2
}

/** ordered tail-vertex ids of a loop (tail of each traversed edge) */
function loopTails(topo: BodyTopology, loop: BodyLoop): BodyVid[] {
  return loop.edges.map((eid, i) => {
    const e = topo.edges[eid]
    return loop.dirs[i] ? e.a : e.b
  })
}

/**
 * THE VALIDITY GATE (2026-09-27, Matt: "In SketchUp when you attempt to pass a plane
 * that would mess up the solid, it stops you … we can get broken geometries"): every
 * push/pull runs on a scratch copy and is only written back when the result is sound
 * — the arrangement invariants hold (no crossing edges, planar oriented loops, no
 * duplicates) AND no edge pierces the interior of a face it isn't part of. A result
 * that fails is a refusal, and `topo` is left exactly as it was. The page uses the
 * refusal to stop the gesture at the obstruction (the last sound distance).
 */
export function extrudeFace(
  topo: BodyTopology,
  fid: BodyFid,
  distFt: number,
  opts: { keepOriginal?: boolean } = {},
): ExtrudeResult {
  const work: BodyTopology = JSON.parse(JSON.stringify(topo))
  const r = extrudeFaceRaw(work, fid, distFt, opts)
  if (!r.ok) return r
  if (invariants(work).length || edgePiercesFace(work)) {
    return { ok: false, error: 'That would pass through the shape — stopped.' }
  }
  topo.vertices = work.vertices
  topo.edges = work.edges
  topo.faces = work.faces
  topo.curves = work.curves
  return r
}

/** does any edge cross through the INTERIOR of a face that doesn't contain it?
 *  (bounding-box prefiltered; touching a face's boundary or plane is fine) */
export function edgePiercesFace(topo: BodyTopology): boolean {
  const faces = Object.entries(topo.faces).map(([fid, f]) => {
    const frame = frameForPlane(f.plane)
    const pts3 = f.loops.flatMap((l) => loopPoints(topo, l))
    const lo = { x: Infinity, y: Infinity, z: Infinity }, hi = { x: -Infinity, y: -Infinity, z: -Infinity }
    for (const q of pts3) {
      lo.x = Math.min(lo.x, q.x); lo.y = Math.min(lo.y, q.y); lo.z = Math.min(lo.z, q.z)
      hi.x = Math.max(hi.x, q.x); hi.y = Math.max(hi.y, q.y); hi.z = Math.max(hi.z, q.z)
    }
    const vset = new Set<BodyVid>()
    for (const l of f.loops) for (const v of loopTails(topo, l)) vset.add(v)
    return { fid, f, frame, lo, hi, vset, rings: f.loops.map((l) => loopPoints(topo, l).map((q) => to2(frame, q))) }
  })
  const tol = WELD_TOL * 2
  for (const e of Object.values(topo.edges)) {
    const a = topo.vertices[e.a], b = topo.vertices[e.b]
    if (!a || !b) continue
    for (const F of faces) {
      if (F.vset.has(e.a) && F.vset.has(e.b)) continue
      if (Math.max(a.x, b.x) < F.lo.x - tol || Math.min(a.x, b.x) > F.hi.x + tol
        || Math.max(a.y, b.y) < F.lo.y - tol || Math.min(a.y, b.y) > F.hi.y + tol
        || Math.max(a.z, b.z) < F.lo.z - tol || Math.min(a.z, b.z) > F.hi.z + tol) continue
      const da = planeDist(F.f.plane, a), db = planeDist(F.f.plane, b)
      if (!((da > tol && db < -tol) || (da < -tol && db > tol))) continue   // must strictly cross the plane
      const t = da / (da - db)
      const x = add(a, scale({ x: b.x - a.x, y: b.y - a.y, z: b.z - a.z }, t))
      const p2 = to2(F.frame, x)
      if (!pointInRegion(p2, F.rings[0], F.rings.slice(1))) continue
      if (distToRingsLocal(p2, F.rings) <= tol) continue                   // grazing the boundary
      return true
    }
  }
  return false
}

function extrudeFaceRaw(
  topo: BodyTopology,
  fid: BodyFid,
  distFt: number,
  opts: { keepOriginal?: boolean } = {},
): ExtrudeResult {
  const F = topo.faces[fid]
  if (!F) return { ok: false, error: 'No face to push/pull here.' }
  // NaN GUARD (the K2a gesture bug): Math.abs(NaN) <= tol is FALSE, so a NaN
  // distance sailed past the tolerance check and wrote non-finite vertices
  if (!Number.isFinite(distFt)) return { ok: false, error: 'Distance is not a number.' }
  if (Math.abs(distFt) <= WELD_TOL) return { ok: false, error: 'Distance is below the weld tolerance.' }
  const n = norm(F.plane.n)
  const offset = scale(n, distFt)
  if (!opts.keepOriginal) {
    const moved = moveFaceAlong(topo, fid, offset)
    if (moved) return moved
  }
  const targetPlane: BodyPlane = { n, d: F.plane.d + distFt }
  const frame = frameForPlane(F.plane)
  const fRings2 = F.loops.map((l) => loopPoints(topo, l).map((q) => to2(frame, q)))
  const ipF = regionInteriorPoint(fRings2[0], fRings2.slice(1))
  if (!ipF) return { ok: false, error: 'Degenerate face.' }

  // ---- classify the landing --------------------------------------------------
  // translation along n keeps (u,v) coordinates identical, so F's own 2D rings
  // describe the cap region in the TARGET plane's frame too
  const targetFrame = frameForPlane(targetPlane)
  let mode: ExtrudeMode = 'out'
  let landing: { fid: BodyFid; face: BodyFace } | null = null
  // scan ALL coplanar faces before deciding — a landing must sit inside exactly
  // ONE face, with no other coplanar face's interior poking into the cap (the
  // partial-landing test: vertices can ride the seam within tolerance while a
  // neighboring piece still overlaps the cap region)
  for (const [gid, G] of Object.entries(topo.faces)) {
    if (gid === fid) continue
    if (!samePlane(G.plane, targetPlane)) continue
    const gRings = G.loops.map((l) => loopPoints(topo, l).map((q) => to2(targetFrame, q)))
    const capInG = pointInRegion(ipF, gRings[0], gRings.slice(1))
    const ipG = regionInteriorPoint(gRings[0], gRings.slice(1))
    const gInCap = !!ipG && pointInRegion(ipG, fRings2[0], fRings2.slice(1))
    if (capInG) {
      if (landing) return { ok: false, error: 'The face lands across several faces — not supported yet.' }
      // full containment required: every cap outer VERTEX and every outer EDGE
      // MIDPOINT inside or on G — midpoints catch the seam-riding case where a
      // neighboring coplanar piece overlaps the cap while all vertices sit on
      // shared boundaries within tolerance (the partial-landing test)
      const probes: Pt2[] = [
        ...fRings2[0],
        ...fRings2[0].map((pt, i) => {
          const q = fRings2[0][(i + 1) % fRings2[0].length]
          return { x: (pt.x + q.x) / 2, y: (pt.y + q.y) / 2 }
        }),
      ]
      const ok = probes.every((pt) =>
        pointInRegion(pt, gRings[0], gRings.slice(1)) || distToRingsLocal(pt, gRings) <= WELD_TOL * 2)
      if (!ok) return { ok: false, error: 'The face lands partly off the opposing face — not supported yet.' }
      landing = { fid: gid, face: G }
    } else if (gInCap) {
      return { ok: false, error: 'The face lands across existing faces — not supported yet.' }
    }
  }
  if (landing) mode = 'through'

  // ---- pushing INTO an adjacent coplanar face's interior → TRIM ------------------
  // (2026-09-26, Matt: subdivided faces "can be pushed or pulled, creating offshoots
  // or voids if the face is one face of a solid"): a side sweep that lands INSIDE a
  // neighbor face (half a box top pushed down; a rectangle drawn against the box
  // edge pushed in) must cut a notch out of that neighbor instead of adding a wall.
  // Collected here; extrudeTrim does the arrangement work.
  const trimIps: BodyPt3[] = []
  for (let li = 0; li < F.loops.length; li++) {
    const tails = loopTails(topo, F.loops[li])
    for (let i = 0; i < tails.length; i++) {
      const a = topo.vertices[tails[i]]
      const b = topo.vertices[tails[(i + 1) % tails.length]]
      const quadIp: BodyPt3 = add(scale(add(a, b), 0.5), scale(n, distFt / 2))
      for (const [hid, H] of Object.entries(topo.faces)) {
        if (hid === fid) continue
        if (Math.abs(planeDist(H.plane, quadIp)) > PLANE_TOL) continue
        const hFrame = frameForPlane(H.plane)
        const hRings = H.loops.map((l) => loopPoints(topo, l).map((q) => to2(hFrame, q)))
        if (pointInRegion(to2(hFrame, quadIp), hRings[0], hRings.slice(1))) { trimIps.push(quadIp); break }
      }
    }
  }
  if (trimIps.length) {
    if (landing) return { ok: false, error: 'Pushing through while cutting into a neighboring face is not supported yet.' }
    if (opts.keepOriginal) return { ok: false, error: 'Ctrl (keep the face) is not supported when the push cuts into a neighboring face.' }
    return extrudeTrim(topo, fid, offset, targetPlane, trimIps, ipF, frame)
  }

  const addedFaces: BodyFid[] = []
  const removedFaces: BodyFid[] = []

  // ---- translated vertices + risers -------------------------------------------
  const vmap = new Map<BodyVid, BodyVid>()
  for (const loop of F.loops) {
    for (const vid of loopTails(topo, loop)) {
      if (vmap.has(vid)) continue
      vmap.set(vid, weldVertex(topo, add(topo.vertices[vid], offset)))
    }
  }
  for (const [vid, vpr] of vmap) {
    if (vid !== vpr) addEdge(topo, vid, vpr)
  }

  // snapshot the original ring edges before anything mutates loops
  const ringEdges = F.loops.flatMap((l) => [...l.edges])

  // ---- cap ---------------------------------------------------------------------
  let capFid: BodyFid | null = null
  if (mode === 'out') {
    const loops: BodyLoop[] = F.loops.map((loop) => {
      const edges: BodyEid[] = []
      const dirs: boolean[] = []
      for (let i = 0; i < loop.edges.length; i++) {
        const e = topo.edges[loop.edges[i]]
        const na = vmap.get(e.a)!
        const nb = vmap.get(e.b)!
        const r = addEdge(topo, na, nb)
        if (!r) return { edges: [], dirs: [] }
        // preserve traversal: stored (na, nb) mirrors (e.a, e.b) unless dedupe flipped it
        const flipped = topo.edges[r.eid].a !== na
        edges.push(r.eid)
        dirs.push(flipped ? !loop.dirs[i] : loop.dirs[i])
      }
      return { edges, dirs }
    })
    if (loops.some((l) => !l.edges.length)) return { ok: false, error: 'Degenerate translated ring.' }
    const outerPts = loops[0].edges.map((eid, i) => topo.vertices[loops[0].dirs[i] ? topo.edges[eid].a : topo.edges[eid].b])
    const refit = planeFromPoints(outerPts)
    capFid = newFid()
    topo.faces[capFid] = {
      plane: refit ?? targetPlane,
      loops,
      ...facePaint(F),
    }
    addedFaces.push(capFid)
  } else {
    // THROUGH: create the translated ring edges PLAINLY first — the side quads
    // build against them below, and only then does the ring retrace through the
    // arrangement (repartitioning the landing face). Ordering matters: inserting
    // the ring while the risers exist but the quads don't would let K0's healer
    // close the vertical loops into side faces EARLY, and the explicit quad
    // construction would then duplicate them (a real bug the through test caught).
    for (const loop of F.loops) {
      const tails = loopTails(topo, loop)
      for (let i = 0; i < tails.length; i++) {
        const a = vmap.get(tails[i])!
        const b = vmap.get(tails[(i + 1) % tails.length])!
        if (a !== b) addEdge(topo, a, b)
      }
    }
  }

  // ---- side faces ----------------------------------------------------------------
  for (const loop of F.loops) {
    for (let i = 0; i < loop.edges.length; i++) {
      const eid = loop.edges[i]
      const e = topo.edges[eid]
      if (!e) continue
      const t = loop.dirs[i] ? e.a : e.b
      const h = loop.dirs[i] ? e.b : e.a
      const tp = vmap.get(t)!
      const hp = vmap.get(h)!
      const riserH = h === hp ? null : findEdgeBetween(topo, h, hp)
      const riserT = t === tp ? null : findEdgeBetween(topo, t, tp)
      const capE = findEdgeBetween(topo, tp, hp)
      if (!riserH || !riserT || !capE) continue // degenerate span (welded flat) — skip
      const capEdge = topo.edges[capE]
      const quadPts = [topo.vertices[t], topo.vertices[h], topo.vertices[hp], topo.vertices[tp]]
      const qPlane = planeFromPoints(quadPts)
      if (!qPlane) continue
      const qf = newFid()
      topo.faces[qf] = {
        plane: qPlane,
        loops: [{
          edges: [eid, riserH, capE, riserT],
          dirs: [
            loop.dirs[i],
            topo.edges[riserH].a === h,        // h → hp
            capEdge.a === hp,                  // hp → tp
            topo.edges[riserT].a === tp,       // tp → t
          ],
        }],
      }
      addedFaces.push(qf)
    }
  }

  // ---- THROUGH completion: retrace the landed ring through the arrangement ------
  // (the quads are faced now, so K0's healer skips their vertical loops), which
  // repartitions the landing face — then delete the punched piece(s)
  if (mode === 'through') {
    for (const loop of F.loops) {
      const tails = loopTails(topo, loop)
      for (let i = 0; i < tails.length; i++) {
        const a = vmap.get(tails[i])!
        const b = vmap.get(tails[(i + 1) % tails.length])!
        if (a === b) continue
        insertSegment(topo, topo.vertices[a], topo.vertices[b])
        // v1 soundness guard: the ring edge must survive as ONE edge
        if (!findEdgeBetween(topo, a, b)) {
          return { ok: false, error: 'The landing crosses existing edges — not supported yet.' }
        }
      }
    }
    for (const [gid, G] of Object.entries(topo.faces)) {
      if (gid === fid) continue
      if (!samePlane(G.plane, targetPlane)) continue
      const gRings = G.loops.map((l) => loopPoints(topo, l).map((q) => to2(targetFrame, q)))
      const ipG = regionInteriorPoint(gRings[0], gRings.slice(1))
      if (ipG && pointInRegion(ipG, fRings2[0], fRings2.slice(1))) {
        delete topo.faces[gid]
        removedFaces.push(gid)
      }
    }
  }

  // ---- consume the source face ---------------------------------------------------
  if (!(opts.keepOriginal && mode === 'out')) {
    delete topo.faces[fid]
    removedFaces.push(fid)
  }

  // ---- coplanar DISSOLVE (the box-top pull: side sweep merges with the neighbor) --
  for (const eid of ringEdges) {
    if (!topo.edges[eid]) continue
    const users = facesUsingEdge(topo, eid)
    if (users.length !== 2) continue
    const [u1, u2] = users
    if (!samePlane(topo.faces[u1].plane, topo.faces[u2].plane)) continue
    const merged = dissolveEdge(topo, eid)
    if (merged) {
      removedFaces.push(u1, u2)
      addedFaces.push(...merged)
    }
  }

  // ---- HEAL the left-behind ring (the lone-face → closed-box bottom) --------------
  // only ring edges with exactly ONE face user heal: a pocket's ring is shared
  // with the surrounding face (two users) and must stay open
  const healSeeds = ringEdges.filter((eid) => topo.edges[eid] && facesUsingEdge(topo, eid).length === 1)
  if (healSeeds.length && !(opts.keepOriginal && mode === 'out')) {
    // re-heal through the arrangement: retracing one ring edge seeds K0's healer
    const seed = topo.edges[healSeeds[0]]
    const r = insertSegment(topo, topo.vertices[seed.a], topo.vertices[seed.b])
    addedFaces.push(...r.addedFaces)
  }

  gcOrphanVertices(topo)
  const alive = (list: BodyFid[]) => list.filter((f) => topo.faces[f])
  return {
    ok: true, mode,
    capFid: capFid && topo.faces[capFid] ? capFid : null,
    addedFaces: [...new Set(alive(addedFaces))],
    removedFaces: [...new Set(removedFaces.filter((f) => !topo.faces[f]))],
  }
}

/**
 * TRIM EXTRUDE — push a face whose side sweep cuts INTO one or more neighbor faces
 * (the notch / void case). Built entirely through the arrangement so K0 does the
 * splitting and healing:
 *   1. insert every riser (ring vertex → its translated copy) and every cap edge
 *      (translated ring) with insertSegment — risers inside a neighbor split it,
 *      risers along an existing edge split that edge, closed planar loops heal:
 *      the cap face and the wall quads for non-trimmed ring edges appear by healing;
 *   2. delete the swept-away pieces: the original face F and, in each trimmed
 *      neighbor, the piece holding that edge's sweep interior point;
 *   3. drop the edges left with no face that belong to the old ring / risers.
 * Verified afterwards: a cap exists, and the arrangement invariants hold —
 * otherwise a refusal (callers always extrude on a clone, so nothing leaks).
 */
function extrudeTrim(
  topo: BodyTopology, fid: BodyFid, offset: BodyPt3, targetPlane: BodyPlane,
  trimIps: BodyPt3[], ipF: Pt2, frame: ReturnType<typeof frameForPlane>,
): ExtrudeResult {
  const F = topo.faces[fid]
  const ringVids = new Set<BodyVid>()
  const segs: [BodyVid, BodyVid][] = []
  for (const loop of F.loops) {
    const tails = loopTails(topo, loop)
    for (let i = 0; i < tails.length; i++) {
      ringVids.add(tails[i])
      segs.push([tails[i], tails[(i + 1) % tails.length]])
    }
  }
  const ringPts = new Map<BodyVid, BodyPt3>()
  for (const v of ringVids) ringPts.set(v, { ...topo.vertices[v] })
  // F's interior point, in 3D, at its ORIGINAL plane (to find F after repartitions)
  const ipF3 = from2(frame, ipF)
  const ipCap = add(ipF3, offset)
  const before = new Set(Object.keys(topo.faces))

  for (const v of ringVids) {
    const p = ringPts.get(v)!
    insertSegment(topo, p, add(p, offset))
  }
  for (const [a, b] of segs) {
    insertSegment(topo, add(ringPts.get(a)!, offset), add(ringPts.get(b)!, offset))
  }

  // swept-away pieces: F (wherever repartition left it) + each trimmed neighbor piece
  const doomed = new Set<BodyFid>()
  const fNow = topo.faces[fid] ? fid : faceAt(topo, ipF3)
  if (fNow) doomed.add(fNow)
  for (const ip of trimIps) {
    const q = faceAt(topo, ip)
    if (q) doomed.add(q)
  }
  for (const d of doomed) delete topo.faces[d]

  // orphaned ring / riser edges (both ends on the old ring or its translated copy)
  const keepV = new Set<BodyVid>(ringVids)
  for (const p of ringPts.values()) {
    const t = add(p, offset)
    for (const [vid, v] of Object.entries(topo.vertices)) {
      const dx = v.x - t.x, dy = v.y - t.y, dz = v.z - t.z
      if (dx * dx + dy * dy + dz * dz <= WELD_TOL * WELD_TOL * 4) keepV.add(vid)
    }
  }
  const onSweep = (vid: BodyVid) => {
    if (keepV.has(vid)) return true
    // a vertex minted mid-riser (a riser split where it crossed something) lies on
    // the segment between a ring point and its translated copy
    const v = topo.vertices[vid]
    for (const p of ringPts.values()) {
      const q = add(p, offset)
      const { d } = pointSegDist(v, p, q)
      if (d <= WELD_TOL * 2) return true
    }
    return false
  }
  for (const [eid, e] of Object.entries(topo.edges)) {
    if (facesUsingEdge(topo, eid).length) continue
    if (onSweep(e.a) && onSweep(e.b)) delete topo.edges[eid]
  }
  gcOrphanVertices(topo)

  const cap = faceAt(topo, ipCap)
  if (!cap) return { ok: false, error: 'The pushed face could not be rebuilt here — not supported yet.' }
  const alive = Object.keys(topo.faces)
  return {
    ok: true, mode: 'out', capFid: cap,
    addedFaces: alive.filter((f) => !before.has(f)),
    removedFaces: [...before].filter((f) => !topo.faces[f]),
  }
}

/**
 * FACE MOVE — slide a face (plus any face coplanar with it that shares its
 * vertices, e.g. a sub-face drawn on a box top) along `offset` when every OTHER
 * face touching the moved vertices contains the offset direction in its own
 * plane: then moving the vertices keeps every face planar and the topology is
 * untouched (a box top pushed down → a shorter box; pulled up → a taller one).
 *
 * Returns null (caller falls through to the extrude rules) when:
 * - the face is FREE (a ring edge with no second face) — a lone face must
 *   extrude into a box, never just translate;
 * - some neighbor is not parallel to the push (the sweep would cut into it);
 * - the move would collapse or turn inside-out any neighboring face (its signed
 *   area in its own frame changes sign or reaches ~0) — e.g. a box top pushed
 *   down to or past the bottom, which stays a refusal/through case.
 */
function moveFaceAlong(topo: BodyTopology, fid: BodyFid, offset: BodyPt3): ExtrudeResult | null {
  const F = topo.faces[fid]
  // free edge → not a move (lone face / open surface edge extrudes); an OUTER
  // edge shared with a COPLANAR face → F is a sub-face drawn on a bigger face,
  // which pushes into a pocket / pulls into a boss, never slides
  for (let li = 0; li < F.loops.length; li++) for (const eid of F.loops[li].edges) {
    const users = facesUsingEdge(topo, eid)
    if (users.length < 2) return null
    if (li === 0 && users.some((u) => u !== fid && samePlane(topo.faces[u].plane, F.plane))) return null
  }
  // closure: F + the coplanar sub-faces drawn INSIDE it (through its hole loops,
  // recursively) move rigidly — a box top with a rectangle drawn on it slides whole
  const movedFaces = new Set<BodyFid>([fid])
  const queue: BodyFid[] = [fid]
  while (queue.length) {
    const G = topo.faces[queue.pop()!]
    for (let li = 1; li < G.loops.length; li++) for (const eid of G.loops[li].edges) {
      for (const hid of facesUsingEdge(topo, eid)) {
        if (movedFaces.has(hid) || !samePlane(topo.faces[hid].plane, F.plane)) continue
        movedFaces.add(hid)
        queue.push(hid)
      }
    }
  }
  const movedV = new Set<BodyVid>()
  for (const gid of movedFaces) for (const loop of topo.faces[gid].loops) for (const vid of loopTails(topo, loop)) movedV.add(vid)
  const facesOfVertex = new Map<BodyVid, Set<BodyFid>>()
  for (const [gid, G] of Object.entries(topo.faces)) {
    for (const loop of G.loops) for (const vid of loopTails(topo, loop)) {
      if (!movedV.has(vid)) continue
      let set = facesOfVertex.get(vid)
      if (!set) facesOfVertex.set(vid, (set = new Set()))
      set.add(gid)
    }
  }
  // every other face touching a moved vertex must contain the offset direction
  const len = Math.sqrt(dot(offset, offset))
  const dirn = scale(offset, 1 / len)
  const stretched = new Set<BodyFid>()
  for (const vid of movedV) for (const hid of facesOfVertex.get(vid) || []) {
    if (movedFaces.has(hid)) continue
    if (Math.abs(dot(norm(topo.faces[hid].plane.n), dirn)) > 1e-6) return null
    stretched.add(hid)
  }
  if (!stretched.size) return null
  // collapse / inversion guard on the stretched faces
  const signedArea = (G: BodyFace, frame: ReturnType<typeof frameForPlane>, moveIt: boolean) => {
    const pts = loopTails(topo, G.loops[0]).map((vid) => {
      const v = topo.vertices[vid]
      return to2(frame, moveIt && movedV.has(vid) ? add(v, offset) : v)
    })
    let a = 0
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i], q = pts[(i + 1) % pts.length]
      a += p.x * q.y - q.x * p.y
    }
    return a / 2
  }
  // WELD ON LANDING (2026-09-27, Matt: "whenever I do a push/pull and get a snap,
  // when I click the move doesn't take"): a snap lands the face EXACTLY on existing
  // corners (a stepped-down half pulled back up to the top, a face pushed flush
  // with a corner), which used to be refused. Moved vertices that land on unmoved
  // ones now WELD: a neighbor that collapses to zero area because of it is removed
  // (the step wall of a step pulled back flush), duplicate edges fold together, and
  // coplanar faces that now share an edge dissolve into one — SketchUp's merge.
  // A neighbor that would collapse or turn inside out WITHOUT welding still refuses.
  const weldTarget = new Map<BodyVid, BodyVid>()
  for (const vid of movedV) {
    const target = add(topo.vertices[vid], offset)
    for (const [wid, w] of Object.entries(topo.vertices)) {
      if (movedV.has(wid)) continue
      const dx = w.x - target.x, dy = w.y - target.y, dz = w.z - target.z
      if (dx * dx + dy * dy + dz * dz <= WELD_TOL * WELD_TOL) { weldTarget.set(vid, wid); break }
    }
  }
  // a moved vertex LANDS when its target is an unmoved vertex (weld) or the interior
  // of an unmoved edge (split there) — either way it joins existing geometry
  const landsOn = new Set<BodyVid>(weldTarget.keys())
  for (const vid of movedV) {
    if (landsOn.has(vid)) continue
    const target = add(topo.vertices[vid], offset)
    for (const e of Object.values(topo.edges)) {
      if (movedV.has(e.a) || movedV.has(e.b)) continue
      const { d, t } = pointSegDist(target, topo.vertices[e.a], topo.vertices[e.b])
      if (d <= WELD_TOL && t > 1e-9 && t < 1 - 1e-9) { landsOn.add(vid); break }
    }
  }
  const collapsing = new Set<BodyFid>()
  for (const hid of stretched) {
    const G = topo.faces[hid]
    const frame = frameForPlane(G.plane)
    const before = signedArea(G, frame, false)
    const after = signedArea(G, frame, true)
    const flat = Math.abs(after) <= WELD_TOL * WELD_TOL * 16
    if (flat) {
      // allowed only when every moved vertex of this face welds onto existing geometry
      const own = loopTails(topo, G.loops[0]).filter((v) => movedV.has(v))
      if (own.length && own.every((v) => landsOn.has(v))) { collapsing.add(hid); continue }
      return { ok: false, error: 'That would collapse the neighboring faces — push a shorter distance.' }
    }
    if (Math.sign(after) !== Math.sign(before)) {
      return { ok: false, error: 'That would collapse the neighboring faces — push a shorter distance.' }
    }
  }
  // every neighbor flattening = the face lands on the far side of the solid (a box
  // top pushed onto its own bottom) — that flattens the body, never a weld
  for (const vid of movedV) topo.vertices[vid] = add(topo.vertices[vid], offset)
  const shift = dot(norm(F.plane.n), offset)
  for (const gid of movedFaces) {
    const G = topo.faces[gid]
    G.plane = { n: G.plane.n, d: G.plane.d + shift * (dot(norm(G.plane.n), norm(F.plane.n)) >= 0 ? 1 : -1) }
  }
  // SETTLE (2026-09-27): after the slide the moved geometry may now TOUCH the rest
  // of the body — corners on corners, corners mid-edge, or the face lying flat on a
  // coplanar face. SketchUp merges all of it; so does this: split edges at landing
  // points, weld coincident vertices, drop back-and-forth spikes and degenerate
  // faces, and when the moved face now lies on another face's plane, cancel the
  // overlap — the face and the piece it lands on both ERASE ("does a face erasure
  // when two faces line up perfectly" — a notch ceiling pushed flush with the top
  // opens the notch full height and notches the top).
  const coplanarNow = Object.keys(topo.faces).filter((gid) => gid !== fid && !movedFaces.has(gid)
    && samePlane(topo.faces[gid].plane, topo.faces[fid].plane))
  if (!landsOn.size && !coplanarNow.length) return { ok: true, mode: 'move', capFid: fid, addedFaces: [], removedFaces: [] }

  const loneBefore = new Set(Object.keys(topo.edges).filter((eid) => !facesUsingEdge(topo, eid).length))
  const removedFaces: BodyFid[] = []
  const welded = new Set<BodyVid>(weldTarget.values())
  // corners arriving mid-edge split that edge
  for (const vid of movedV) {
    if (weldTarget.has(vid)) continue
    const v = topo.vertices[vid]
    for (const [eid, e] of Object.entries(topo.edges)) {
      if (movedV.has(e.a) || movedV.has(e.b)) continue
      const { d, t } = pointSegDist(v, topo.vertices[e.a], topo.vertices[e.b])
      if (d <= WELD_TOL && t > 1e-9 && t < 1 - 1e-9) { splitEdge(topo, eid, vid); welded.add(vid); break }
    }
  }
  // existing corners now lying mid-way along a moved edge split THAT edge
  for (const [wid, w] of Object.entries(topo.vertices)) {
    if (movedV.has(wid) || welded.has(wid)) continue
    for (const [eid, e] of Object.entries(topo.edges)) {
      if (!(movedV.has(e.a) || movedV.has(e.b)) || e.a === wid || e.b === wid) continue
      const { d, t } = pointSegDist(w, topo.vertices[e.a], topo.vertices[e.b])
      if (d <= WELD_TOL && t > 1e-9 && t < 1 - 1e-9) { splitEdge(topo, eid, wid); welded.add(wid); break }
    }
  }
  for (const hid of collapsing) { delete topo.faces[hid]; removedFaces.push(hid) }
  weldVertices(topo, weldTarget)
  removeSpikes(topo)
  for (const [gid, G] of Object.entries(topo.faces)) {
    if (G.loops.some((l) => l.edges.length < 3)) { delete topo.faces[gid]; removedFaces.push(gid) }
  }
  // face ERASURE: the moved face lies on a coplanar face — retrace its edges so they
  // partition that face, then the moved face and every coplanar piece inside it go
  const Fm = topo.faces[fid]
  if (Fm) {
    const fFrame = frameForPlane(Fm.plane)
    const fRings = Fm.loops.map((l) => loopPoints(topo, l).map((q) => to2(fFrame, q)))
    const ip = regionInteriorPoint(fRings[0], fRings.slice(1))
    const others = Object.keys(topo.faces).filter((gid) => gid !== fid && samePlane(topo.faces[gid].plane, Fm.plane))
    if (ip && others.length) {
      const ringPts = Fm.loops.map((l) => loopPoints(topo, l).map((q) => ({ ...q })))
      for (const ring of ringPts) {
        for (let i = 0; i < ring.length; i++) insertSegment(topo, ring[i], ring[(i + 1) % ring.length])
      }
      const ip3 = from2(fFrame, ip)
      const fNow = topo.faces[fid] ? fid : faceAt(topo, ip3)
      if (fNow) {
        const F2 = topo.faces[fNow]
        const fr2 = frameForPlane(F2.plane)
        const r2 = F2.loops.map((l) => loopPoints(topo, l).map((q) => to2(fr2, q)))
        const overlap: BodyFid[] = []
        for (const [gid, G] of Object.entries(topo.faces)) {
          if (gid === fNow || !samePlane(G.plane, F2.plane)) continue
          const gr = G.loops.map((l) => loopPoints(topo, l).map((q) => to2(fr2, q)))
          const gip = regionInteriorPoint(gr[0], gr.slice(1))
          if (gip && pointInRegion(gip, r2[0], r2.slice(1))) overlap.push(gid)
        }
        if (overlap.length) {
          delete topo.faces[fNow]
          removedFaces.push(fNow)
          for (const g of overlap) { delete topo.faces[g]; removedFaces.push(g) }
        }
      }
    }
  }
  // edges this left with no face (never a lone line the user drew before) go
  for (const eid of Object.keys(topo.edges)) {
    if (loneBefore.has(eid)) continue
    if (!facesUsingEdge(topo, eid).length) delete topo.edges[eid]
  }
  // coplanar seams through the touched corners dissolve (the two top halves → one)
  const touched = new Set<BodyVid>([...movedV, ...welded])
  const addedFaces: BodyFid[] = []
  for (const eid of Object.keys(topo.edges)) {
    const e = topo.edges[eid]
    if (!e || !(touched.has(e.a) || touched.has(e.b))) continue
    const users = facesUsingEdge(topo, eid)
    if (users.length !== 2 || !samePlane(topo.faces[users[0]].plane, topo.faces[users[1]].plane)) continue
    const merged = dissolveEdge(topo, eid)
    if (merged) { removedFaces.push(...users); addedFaces.push(...merged) }
  }
  gcOrphanVertices(topo)
  // a landing that erases EVERYTHING (a box top pushed onto its own bottom) flattens
  // the body — refuse; a boss pushed back flush merges into its surroundings instead
  if (!Object.keys(topo.faces).length) {
    return { ok: false, error: 'That would collapse the neighboring faces — push a shorter distance.' }
  }
  const cap = topo.faces[fid] ? fid : null
  return {
    ok: true, mode: 'move', capFid: cap,
    addedFaces: addedFaces.filter((f) => topo.faces[f]),
    removedFaces: [...new Set(removedFaces.filter((f) => !topo.faces[f]))],
  }
}

/** drop back-and-forth spikes from every loop: an edge traversed and immediately
 *  traversed back (a strip that welded shut) contributes nothing to the region */
export function removeSpikes(topo: BodyTopology): void {
  for (const G of Object.values(topo.faces)) {
    for (const loop of G.loops) {
      let changed = true
      while (changed && loop.edges.length >= 2) {
        changed = false
        const n = loop.edges.length
        for (let i = 0; i < n; i++) {
          const j = (i + 1) % n
          if (loop.edges[i] === loop.edges[j] && loop.dirs[i] !== loop.dirs[j]) {
            const hi = Math.max(i, j), lo = Math.min(i, j)
            loop.edges.splice(hi, 1); loop.dirs.splice(hi, 1)
            loop.edges.splice(lo, 1); loop.dirs.splice(lo, 1)
            changed = true
            break
          }
        }
      }
    }
  }
}

/** merge each vertex in `map` (from → to) into its target: edges re-point, edges
 *  that became zero-length drop out of every loop, edges that became duplicates of
 *  another edge fold into it (loop directions corrected) */
export function weldVertices(topo: BodyTopology, map: Map<BodyVid, BodyVid>): void {
  for (const e of Object.values(topo.edges)) {
    if (map.has(e.a)) e.a = map.get(e.a)!
    if (map.has(e.b)) e.b = map.get(e.b)!
  }
  for (const from of map.keys()) delete topo.vertices[from]
  const zero = new Set<BodyEid>()
  const keepByKey = new Map<string, BodyEid>()
  const replace = new Map<BodyEid, { eid: BodyEid; flip: boolean }>()
  for (const [eid, e] of Object.entries(topo.edges)) {
    if (e.a === e.b) { zero.add(eid); continue }
    const k = e.a < e.b ? e.a + '|' + e.b : e.b + '|' + e.a
    const keep = keepByKey.get(k)
    if (!keep) { keepByKey.set(k, eid); continue }
    replace.set(eid, { eid: keep, flip: topo.edges[keep].a !== e.a })
  }
  for (const G of Object.values(topo.faces)) {
    for (const loop of G.loops) {
      const edges: BodyEid[] = []
      const dirs: boolean[] = []
      for (let i = 0; i < loop.edges.length; i++) {
        const eid = loop.edges[i]
        if (zero.has(eid)) continue
        const r = replace.get(eid)
        edges.push(r ? r.eid : eid)
        dirs.push(r && r.flip ? !loop.dirs[i] : loop.dirs[i])
      }
      loop.edges = edges
      loop.dirs = dirs
    }
  }
  for (const eid of zero) delete topo.edges[eid]
  for (const eid of replace.keys()) delete topo.edges[eid]
}

/**
 * Merge the two coplanar faces sharing eid into one (SketchUp's coplanar-edge
 * dissolve): drop every edge shared by exactly these two faces, re-trace the
 * union region on the shared plane, and rebuild. Returns the new fids, or null
 * when the edge isn't a two-user coplanar seam.
 */
export function dissolveEdge(topo: BodyTopology, eid: BodyEid): BodyFid[] | null {
  const users = facesUsingEdge(topo, eid)
  if (users.length !== 2) return null
  const [aFid, bFid] = users
  const A = topo.faces[aFid]
  const B = topo.faces[bFid]
  if (!samePlane(A.plane, B.plane)) return null

  const aEdges = new Set(A.loops.flatMap((l) => l.edges))
  const bEdges = new Set(B.loops.flatMap((l) => l.edges))
  const shared = [...aEdges].filter((x) => bEdges.has(x))
  const keep = [...new Set([...aEdges, ...bEdges])].filter((x) => !shared.includes(x))
  if (keep.length < 3) return null

  const frame = frameForPlane(A.plane)
  const nodeIds = new Set<BodyVid>()
  const tEdges: TraceEdge[] = []
  for (const ke of keep) {
    const e = topo.edges[ke]
    if (!e) continue
    nodeIds.add(e.a)
    nodeIds.add(e.b)
    tEdges.push({ id: ke, a: e.a, b: e.b })
  }
  const nodes: TraceNode[] = [...nodeIds].map((vid) => ({ id: vid, pt: to2(frame, topo.vertices[vid]) }))
  const traced = traceFaces(nodes, tEdges)
  if (!traced.length) return null

  delete topo.faces[aFid]
  delete topo.faces[bFid]
  const out: BodyFid[] = []
  for (const t of traced) {
    const nf = newFid()
    topo.faces[nf] = {
      plane: A.plane,
      loops: [
        { edges: [...t.outer.edges], dirs: [...t.outer.dirs] },
        ...t.holes.map((h) => ({ edges: [...h.edges], dirs: [...h.dirs] })),
      ],
      ...facePaint(A.frontMat !== undefined || A.backMat !== undefined ? A : B),
    }
    out.push(nf)
  }
  // the seam edges belong to no face now — SketchUp removes them outright
  for (const se of shared) {
    if (facesUsingEdge(topo, se).length === 0) delete topo.edges[se]
  }
  gcOrphanVertices(topo)
  return out
}

function distToRingsLocal(pt: Pt2, rings: Pt2[][]): number {
  let best = Infinity
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i]
      const b = ring[(i + 1) % ring.length]
      const abx = b.x - a.x
      const aby = b.y - a.y
      const l2 = abx * abx + aby * aby
      let t = l2 > 0 ? ((pt.x - a.x) * abx + (pt.y - a.y) * aby) / l2 : 0
      t = Math.max(0, Math.min(1, t))
      const dx = pt.x - (a.x + abx * t)
      const dy = pt.y - (a.y + aby * t)
      best = Math.min(best, Math.sqrt(dx * dx + dy * dy))
    }
  }
  return best
}
